import { Box, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';

import type { GenericDataType } from '@/components/charts/charts.types';
import HorizontalBarChart from '@/components/charts/HorizontalBarChart';
import { REPORTS_COLORS } from '@/constants/chartcolors.constants';
import REPORT_PARAGRAPHS from '@/constants/reportparagraph.constants';
import ReportsTemplateBoxContainer from '@/features/ReportsTemplateBoxContainer/ReportsTemplateBoxContainer';
import ReportParagraph from '@/features/ReportsTemplateParagraph/ReportsParagraph';
import ReportsTemplateSectionTitle from '@/features/ReportsTemplateSectionTitle/ReportsTemplateSectionTitle';
import ReportsTemplateStatBox from '@/features/ReportsTemplateStatBox/ReportsTemplateStatBox';
import { REPORTS_LOG_EVENTS_HORIZONTAL_BAR_SERIES } from '@/pages/ReportsTemplates/ReportsTemplates.constants';
import { formatLargeNumber } from '@/utils/Common.utils';

import useStyles from '../ReportsPages.styles';
import { useFetchReportsEventsMetricsData } from './EventsPages.hooks';

const EventsPage3 = () => {
  const { classes } = useStyles();

  // Fetch data for log events metrics
  const { data: logEventsWindowsUserLockouts } =
    useFetchReportsEventsMetricsData({
      metricField: 'windows_user_lockouts',
      eventType: 'log'
    });
  const { data: logEventsWindowsEvents } = useFetchReportsEventsMetricsData({
    metricField: 'windows_events',
    eventType: 'log'
  });
  const { data: logEventsUsers } = useFetchReportsEventsMetricsData({
    metricField: 'users',
    eventType: 'log'
  });
  const { data: logEventsUserFailedLogins } = useFetchReportsEventsMetricsData({
    metricField: 'user_failed_logins',
    eventType: 'log'
  });
  const { data: logEventsSidecarAgents } = useFetchReportsEventsMetricsData({
    metricField: 'sidecar_agents',
    eventType: 'log'
  });

  // Log Events by Windows User Lockouts
  const logEventsWindowsUserLockoutsData = useMemo(() => {
    const groupTopData = (logEventsWindowsUserLockouts ?? []).reduce(
      (acc, { count, user_name }) => {
        if (!user_name) return acc; // Skip if user_name is undefined
        if (!acc[user_name]) {
          acc[user_name] = { name: user_name, data: [0] };
        }
        (acc[user_name].data as number[])[0] += count;
        return acc;
      },
      {} as GenericDataType
    );

    const sortedTop = Object.values(groupTopData)
      .sort((a, b) => b.data[0] - a.data[0])
      .slice(0, 5);

    return sortedTop.map((item) => ({
      ...item,
      count: item.data[0],
      color: REPORTS_COLORS.LOG_EVENTS
    }));
  }, [logEventsWindowsUserLockouts]);

  // Log Events by Windows Events
  const logEventsWindowsEventsData = useMemo(() => {
    const groupTopData = (logEventsWindowsEvents ?? []).reduce(
      (acc, { count, event_id }) => {
        if (!event_id) return acc; // Skip if event_id is undefined
        if (!acc[event_id]) {
          acc[event_id] = { name: event_id, data: [0] };
        }
        (acc[event_id].data as number[])[0] += count;
        return acc;
      },
      {} as GenericDataType
    );

    const sortedTop = Object.values(groupTopData)
      .sort((a, b) => b.data[0] - a.data[0])
      .slice(0, 5);

    return sortedTop.map((item) => ({
      ...item,
      count: item.data[0],
      color: REPORTS_COLORS.LOG_EVENTS
    }));
  }, [logEventsWindowsEvents]);

  // Log Events by Users
  const logEventsUsersData = useMemo(() => {
    const groupTopData = (logEventsUsers ?? []).reduce(
      (acc, { count, user_name }) => {
        if (!user_name) return acc; // Skip if user_name is undefined
        if (!acc[user_name]) {
          acc[user_name] = { name: user_name, data: [0] };
        }
        (acc[user_name].data as number[])[0] += count;
        return acc;
      },
      {} as GenericDataType
    );

    const sortedTop = Object.values(groupTopData)
      .sort((a, b) => b.data[0] - a.data[0])
      .slice(0, 5);

    return sortedTop.map((item) => ({
      ...item,
      count: item.data[0],
      color: REPORTS_COLORS.LOG_EVENTS
    }));
  }, [logEventsUsers]);

  // Log Events by User Failed Logins
  const logEventsUserFailedLoginsData = useMemo(() => {
    const groupTopData = (logEventsUserFailedLogins ?? []).reduce(
      (acc, { count, user_name }) => {
        if (!user_name) return acc; // Skip if user_name is undefined
        if (!acc[user_name]) {
          acc[user_name] = { name: user_name, data: [0] };
        }
        (acc[user_name].data as number[])[0] += count;
        return acc;
      },
      {} as GenericDataType
    );

    const sortedTop = Object.values(groupTopData)
      .sort((a, b) => b.data[0] - a.data[0])
      .slice(0, 5);

    return sortedTop.map((item) => ({
      ...item,
      count: item.data[0],
      color: REPORTS_COLORS.LOG_EVENTS
    }));
  }, [logEventsUserFailedLogins]);

  // Log Events by Sidecar Agents
  const logEventsSidecarAgentsData = useMemo(() => {
    const groupTopData = (logEventsSidecarAgents ?? []).reduce(
      (acc, { count, host_name }) => {
        if (!host_name) return acc; // Skip if host_name is undefined
        if (!acc[host_name]) {
          acc[host_name] = { name: host_name, data: [0] };
        }
        (acc[host_name].data as number[])[0] += count;
        return acc;
      },
      {} as GenericDataType
    );

    const sortedTop = Object.values(groupTopData)
      .sort((a, b) => b.data[0] - a.data[0])
      .slice(0, 5);

    return sortedTop.map((item) => ({
      ...item,
      count: item.data[0],
      color: REPORTS_COLORS.LOG_EVENTS
    }));
  }, [logEventsSidecarAgents]);

  // Unique Sidecar Agent Count
  const uniqueSidecarAgentCount = useMemo(() => {
    const set = new Set(
      (logEventsSidecarAgents ?? []).map((item) => item.host_name)
    );
    return set.size;
  }, [logEventsSidecarAgents]);

  return (
    <div className={classes.reportsPageContainer}>
      <ReportsTemplateSectionTitle>Log Events</ReportsTemplateSectionTitle>
      <Box sx={{ flexDirection: 'row', display: 'flex', gap: 2 }}>
        <Box sx={{ width: '49%' }}>
          <Stack spacing={2}>
            <div
              data-testid="top-windows-user-lockouts"
              style={{ height: 200 }}
            >
              <Typography className={classes.reportsPreviewSubTitle}>
                Top Windows User Lockouts
              </Typography>
              <HorizontalBarChart
                height={165}
                data={logEventsWindowsUserLockoutsData}
                series={REPORTS_LOG_EVENTS_HORIZONTAL_BAR_SERIES}
              />
            </div>
            <ReportParagraph fontSize="12px">
              {REPORT_PARAGRAPHS.topWindowsUserLockoutsDescription}
            </ReportParagraph>
            <div style={{ height: 20 }} />
            <div data-testid="top-users" style={{ height: 200 }}>
              <Typography className={classes.reportsPreviewSubTitle}>
                Top Users
              </Typography>
              <HorizontalBarChart
                height={165}
                data={logEventsUsersData}
                series={REPORTS_LOG_EVENTS_HORIZONTAL_BAR_SERIES}
              />
            </div>
            <ReportParagraph fontSize="12px">
              {REPORT_PARAGRAPHS.topUsersDescription}
            </ReportParagraph>
            <div data-testid="top-sidecar-agents" style={{ height: 200 }}>
              <Typography className={classes.reportsPreviewSubTitle}>
                Top Sidecar Agents
              </Typography>
              <HorizontalBarChart
                height={165}
                data={logEventsSidecarAgentsData}
                series={REPORTS_LOG_EVENTS_HORIZONTAL_BAR_SERIES}
              />
            </div>
            <ReportParagraph fontSize="12px">
              {REPORT_PARAGRAPHS.topSidecarAgentsDescription}
            </ReportParagraph>
          </Stack>
        </Box>
        <div style={{ width: '2%' }}>
          <Box
            sx={{
              backgroundColor: '#D1E2D5',
              width: 2,
              height: '100%'
            }}
          />
        </div>
        <Box sx={{ width: '49%' }}>
          <Stack spacing={2}>
            <div data-testid="top-windows-events" style={{ height: 200 }}>
              <Typography className={classes.reportsPreviewSubTitle}>
                Top Windows Events
              </Typography>
              <HorizontalBarChart
                height={165}
                data={logEventsWindowsEventsData}
                series={REPORTS_LOG_EVENTS_HORIZONTAL_BAR_SERIES}
              />
            </div>
            <ReportParagraph fontSize="12px">
              {REPORT_PARAGRAPHS.topWindowsEventsDescription}
            </ReportParagraph>
            <div style={{ height: 20 }} />
            <div data-testid="top-user-failed-logins" style={{ height: 200 }}>
              <Typography className={classes.reportsPreviewSubTitle}>
                Top User Failed Logins
              </Typography>
              <HorizontalBarChart
                height={165}
                data={logEventsUserFailedLoginsData}
                series={REPORTS_LOG_EVENTS_HORIZONTAL_BAR_SERIES}
              />
            </div>
            <ReportParagraph fontSize="12px">
              {REPORT_PARAGRAPHS.topUserFailedLoginsDescription}
            </ReportParagraph>
            <ReportsTemplateBoxContainer
              height="100%"
              subChildren={REPORT_PARAGRAPHS.totalSidecarAgentsDescription}
            >
              <ReportsTemplateStatBox
                title="Total Sidecar Agents"
                value={formatLargeNumber(uniqueSidecarAgentCount, true)}
              />
            </ReportsTemplateBoxContainer>
          </Stack>
        </Box>
      </Box>
    </div>
  );
};

export default EventsPage3;
